import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useCustomers } from '../context/CustomerContext';
import ReactECharts from 'echarts-for-react';
import { format, subDays, isAfter, startOfWeek, endOfWeek, eachDayOfInterval, subMonths, startOfMonth } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiUsers, FiCalendar, FiTarget, FiDollarSign, FiBarChart2, FiPieChart, FiActivity } = FiIcons;

function Analytics() {
  const { state } = useCustomers();
  const { customers, leads, sales, interactions } = state;
  const [timeRange, setTimeRange] = useState('30');

  // Calculate time-based metrics
  const today = new Date();
  const rangeStart = subDays(today, parseInt(timeRange));
  
  const recentCustomers = customers.filter(c => 
    isAfter(new Date(c.createdAt), rangeStart)
  );

  const recentSales = sales.filter(s => 
    isAfter(new Date(s.saleDate), rangeStart)
  );

  const recentLeads = leads.filter(l => 
    isAfter(new Date(l.createdAt), rangeStart)
  );

  // Calculate revenue metrics
  const totalRevenue = recentSales.reduce((sum, sale) => sum + (sale.salePrice || 0), 0);
  const avgDealSize = recentSales.length > 0 ? totalRevenue / recentSales.length : 0;
  
  // Calculate conversion metrics
  const convertedLeads = recentLeads.filter(l => l.status === 'converted').length;
  const conversionRate = recentLeads.length > 0 
    ? (convertedLeads / recentLeads.length * 100).toFixed(1) 
    : 0;

  // Key Performance Indicators
  const kpis = [
    {
      name: 'Total Revenue',
      value: `$${totalRevenue.toLocaleString()}`,
      change: '+12%',
      icon: FiDollarSign,
      color: 'bg-green-500'
    },
    {
      name: 'New Customers',
      value: recentCustomers.length,
      change: '+8%',
      icon: FiUsers,
      color: 'bg-blue-500'
    },
    {
      name: 'Conversion Rate',
      value: `${conversionRate}%`,
      change: '+5%',
      icon: FiTarget,
      color: 'bg-purple-500'
    },
    {
      name: 'Avg Deal Size',
      value: `$${Math.round(avgDealSize).toLocaleString()}`,
      change: '+15%',
      icon: FiTrendingUp,
      color: 'bg-orange-500'
    }
  ];

  // Prepare chart data for Sales Trend
  const salesTrendData = Array.from({ length: 12 }, (_, i) => {
    const month = subMonths(today, i);
    const monthSales = sales.filter(sale => 
      new Date(sale.saleDate).getMonth() === month.getMonth() &&
      new Date(sale.saleDate).getFullYear() === month.getFullYear()
    );
    return {
      month: format(month, 'MMM'),
      revenue: monthSales.reduce((sum, sale) => sum + (sale.salePrice || 0), 0),
      count: monthSales.length
    };
  }).reverse();

  const salesTrendOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: salesTrendData.map(d => d.month),
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisTick: { show: false },
      axisLabel: { color: '#6b7280' }
    },
    yAxis: [
      {
        type: 'value',
        name: 'Revenue',
        axisLabel: {
          formatter: (value) => `$${value/1000}k`,
          color: '#6b7280'
        },
        splitLine: {
          lineStyle: { color: '#f3f4f6' }
        }
      },
      {
        type: 'value',
        name: 'Count',
        axisLabel: {
          color: '#6b7280'
        },
        splitLine: { show: false }
      }
    ],
    series: [
      {
        name: 'Revenue',
        type: 'bar',
        data: salesTrendData.map(d => d.revenue),
        itemStyle: {
          color: '#3b82f6',
          borderRadius: [4, 4, 0, 0]
        }
      },
      {
        name: 'Sales Count',
        type: 'line',
        yAxisIndex: 1,
        data: salesTrendData.map(d => d.count),
        symbolSize: 6,
        itemStyle: { color: '#10b981' },
        lineStyle: { width: 2 }
      }
    ]
  };

  // Lead Source Distribution
  const leadSourceData = leads.reduce((acc, lead) => {
    acc[lead.source] = (acc[lead.source] || 0) + 1;
    return acc;
  }, {});

  const leadSourceOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      right: 10,
      top: 'center',
      textStyle: { color: '#6b7280' }
    },
    series: [
      {
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['40%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 4,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: false
        },
        emphasis: {
          label: {
            show: true,
            fontSize: '14',
            fontWeight: 'bold'
          }
        },
        data: Object.entries(leadSourceData).map(([name, value]) => ({
          name,
          value
        }))
      }
    ]
  };

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Performance Analytics</h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="180">Last 180 days</option>
        </select>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpis.map((kpi, index) => (
          <motion.div
            key={kpi.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{kpi.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{kpi.value}</p>
                <div className="flex items-center mt-2">
                  <span className={`text-sm ${kpi.change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                    {kpi.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-2">vs last period</span>
                </div>
              </div>
              <div className={`${kpi.color} p-3 rounded-lg`}>
                <SafeIcon icon={kpi.icon} className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Trend Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Sales Performance</h3>
            <SafeIcon icon={FiBarChart2} className="w-5 h-5 text-gray-400" />
          </div>
          <ReactECharts option={salesTrendOption} style={{ height: '400px' }} />
        </motion.div>

        {/* Lead Source Distribution Chart */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Lead Sources</h3>
            <SafeIcon icon={FiPieChart} className="w-5 h-5 text-gray-400" />
          </div>
          <ReactECharts option={leadSourceOption} style={{ height: '400px' }} />
        </motion.div>
      </div>

      {/* Performance Metrics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Key Metrics</h3>
          <SafeIcon icon={FiActivity} className="w-5 h-5 text-gray-400" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Sales Metrics</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Sales</span>
                <span className="font-medium">{recentSales.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg Time to Close</span>
                <span className="font-medium">12 days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Revenue per Sale</span>
                <span className="font-medium">${Math.round(avgDealSize).toLocaleString()}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Lead Metrics</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">New Leads</span>
                <span className="font-medium">{recentLeads.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Conversion Rate</span>
                <span className="font-medium">{conversionRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg Response Time</span>
                <span className="font-medium">2.5 hours</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Customer Metrics</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">New Customers</span>
                <span className="font-medium">{recentCustomers.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Repeat Customers</span>
                <span className="font-medium">24%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Customer Satisfaction</span>
                <span className="font-medium">4.8/5.0</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default Analytics;